import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, ArrowRight, CaretDown, CaretUp, Plus, ShieldCheck, CalendarBlank, GoogleLogo, MetaLogo, MagnifyingGlass, Sparkle, ShoppingCart, UsersThree, Buildings, Lightning, Robot } from '@phosphor-icons/react';
import { Container, Title, Text, Button, Paper, Badge, Group, Switch, Slider, SegmentedControl } from '@mantine/core';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';
import Footer from '../components/Footer';
import { formatMoney, currencySymbol } from '../utils/money';

// Pakiety rozliczane wg liczby zarzadzanych kampanii i podlaczonych kont reklamowych.
// Im wiekszy pakiet, tym nizszy koszt za pojedyncza kampanie (perCampaign). Wszystkie
// funkcje agenta sa dostepne w kazdym pakiecie, plany roznia się tylko limitami.
// Etykiety (nazwa, CTA, note) sa tlumaczone przez i18n wg `key`/`ctaKey`/`noteKey`.
const PACKAGES = [
  { key: 'micro',    price: 79,   campaigns: 1,    accounts: 1,    budgetMaxDaily: 50, perCampaign: 79, cta: { ctaKey: 'startFree', to: '/rejestracja', variant: 'outline' }, noteKey: 'trial' },
  { key: 'start',    price: 299,  campaigns: 3,    accounts: 2,    perCampaign: 100, cta: { ctaKey: 'startFree', to: '/rejestracja', variant: 'filled' }, noteKey: 'trial' },
  { key: 'standard', price: 599,  campaigns: 10,   accounts: 4,    perCampaign: 60,  popular: true, cta: { ctaKey: 'startFree', to: '/rejestracja', variant: 'filled' }, noteKey: 'trial' },
  { key: 'pro',      price: 999,  campaigns: 25,   accounts: 8,    perCampaign: 40,  cta: { ctaKey: 'startFree', to: '/rejestracja', variant: 'filled' }, noteKey: 'trial' },
  { key: 'agencja',  price: null, campaigns: null, accounts: null, perCampaign: null, cta: { ctaKey: 'contact', to: '/kontakt', variant: 'outline' }, noteKey: 'customScale' },
];

// Cennik kampanii produktowych (PLA / Google Shopping) wg liczby produktow w feedzie.
// Liczony oddzielnie od pakietow Search/Meta: wieksz feed = wiecej synchronizacji i optymalizacji.
// Ceny sa stale (nie tlumaczone); etykiety `mark`/`range` przychodza z i18n (pricing.pla.tiers).
const PLA_PRICES = [299, 599, 899, 1299, 1699, 2199, null];

// Formatowanie ceny ze spacja jako separatorem tysiecy (np. 1 199), niezaleznie od locale.
const fmtPrice = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

// Kursy do przeliczenia cennika PLN -> EUR/USD (stale, lekko zawyzone; zgodne z
// backendem: settings.DROID_FX). Cennik publiczny (/cennik) liczy je po stronie
// klienta, bo endpoint plan-status wymaga logowania.
const FX = { EUR: 4.00, USD: 3.60 };
const priceIn = (pln, cur) => {
  if (pln == null) return null;
  if (cur === 'PLN' || !FX[cur]) return pln;
  return Math.round((pln / FX[cur]) * 100) / 100;
};
// Liczba w stylu PL (spacja=tysiace, przecinek=dziesietne), bez zaokraglania groszy.
const fmtNum = (n) => Number(n).toLocaleString('pl-PL', { maximumFractionDigits: 2 });

// Ikony typow kampanii (kolejnosc odpowiada pricing.campaignTypes.items z i18n).
const CAMPAIGN_TYPE_ICONS = [MagnifyingGlass, Sparkle, ShoppingCart, MetaLogo, UsersThree];

export default function PricingPage() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState(null);
  const [schedulerAddon, setSchedulerAddon] = useState(false);
  const [plaMode, setPlaMode] = useState(false);
  const [plaTier, setPlaTier] = useState(0);
  const [currency, setCurrency] = useState('PLN');

  const plaTiers = t('pricing.pla.tiers', { returnObjects: true });
  const faqItems = t('pricing.faq.items', { returnObjects: true });
  const features = t('pricing.everything.features', { returnObjects: true });
  const campaignTypes = t('pricing.campaignTypes.items', { returnObjects: true });
  const forBusiness = t('pricing.business.forBusiness', { returnObjects: true });
  const forAgency = t('pricing.business.forAgency', { returnObjects: true });
  const schedulerPoints = t('pricing.addons.scheduler.points', { returnObjects: true });
  const monitoringChecks = t('pricing.monitoring.checks', { returnObjects: true });
  const implementationPoints = t('pricing.implementation.points', { returnObjects: true });
  const agencyItems = t('pricing.agency.items', { returnObjects: true });

  const plaPrice = PLA_PRICES[plaTier];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-[#F6F5F4]">
      <SEO title={t('pricing.seo.title')} description={t('pricing.seo.description')} path="/cennik" />
      <Navbar />

      {/* Pricing header */}
      <section className="bg-[#F6F5F4] pt-16 pb-8">
        <Container size="xl" ta="center">
          <Title
            order={1}
            mb="xs"

            fz={{ base: 30, md: 48 }}
          >
            {t('pricing.header.title')}
          </Title>
          <Text c="dimmed" size="lg" maw={680} mx="auto">
            {t('pricing.header.subtitle')}
          </Text>
          <Group justify="center" gap="md" mt="sm">
            <Group gap={6} style={{ opacity: 0.7 }}>
              <GoogleLogo size={18} weight="bold" style={{ color: '#4285F4' }} />
              <Text size="sm" c="dimmed">{t('pricing.header.googleAds')}</Text>
            </Group>
            <Group gap={6} style={{ opacity: 0.7 }}>
              <MetaLogo size={18} weight="bold" style={{ color: '#1877F2' }} />
              <Text size="sm" c="dimmed">{t('pricing.header.metaAds')}</Text>
            </Group>
          </Group>
          <Group justify="center" mt="md">
            <Badge
              variant="light"
              color="brand"
              size="lg"
              radius="xl"
              leftSection={<div className="w-2 h-2 rounded-full bg-green-500" />}
            >
              {t('pricing.header.freeBadge')}
            </Badge>
          </Group>
        </Container>
      </section>

      {/* Toggle: pakiety Search/Meta vs cennik PLA / Shopping */}
      <section className="bg-[#F6F5F4] pt-2 pb-2">
        <Container size="xl">
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-1 p-1 bg-white border border-gray-200 rounded-full">
              <button
                onClick={() => setPlaMode(false)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${!plaMode ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:text-gray-900'}`}
              >
                {t('pricing.toggle.searchMeta')}
              </button>
              <button
                onClick={() => setPlaMode(true)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${plaMode ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:text-gray-900'}`}
              >
                {t('pricing.toggle.shopping')}
              </button>
            </div>
          </div>
          <Text ta="center" c="dimmed" size="sm" mt="md" maw={620} mx="auto">
            {plaMode
              ? t('pricing.toggle.noteShopping')
              : t('pricing.toggle.noteSearchMeta')}
          </Text>
          <Group justify="center" mt="sm">
            <SegmentedControl
              size="xs"
              value={currency}
              onChange={setCurrency}
              data={['PLN', 'EUR', 'USD'].map(c => ({ value: c, label: c }))}
            />
          </Group>
        </Container>
      </section>

      {/* Pricing: PLA / Shopping (feed-based) */}
      {plaMode && (
      <section className="bg-[#F6F5F4] py-10">
        <Container size="xs">
          <Paper radius="lg" p="xl" withBorder className="border-2 border-indigo-300 shadow-lg" style={{ position: 'relative' }}>
            <Badge
              variant="filled"
              color="brand"
              size="sm"
              radius="sm"
              style={{ position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)' }}
            >
              {t('pricing.pla.badge')}
            </Badge>
            <Text size="sm" c="indigo.6" fw={600} mb="xs">{t('pricing.pla.subtitle')}</Text>

            <div className="flex items-end gap-1" style={{ minHeight: 56 }}>
              {plaTier === 0 ? (
                <Text span fz={34} fw={900} c="dark" lh={1.3}>{t('pricing.pla.includedInPackage')}</Text>
              ) : plaPrice === null ? (
                <Text span fz={34} fw={900} c="dark" lh={1.3}>{t('pricing.common.custom')}</Text>
              ) : (
                <>
                  <Text span fz={44} fw={900} c="dark" lh={1.1}>{fmtNum(priceIn(plaPrice, currency))}</Text>
                  <Text span fz={18} c="dimmed" lh={1.7}>{currencySymbol(currency)}</Text>
                  <Text span size="sm" c="dimmed" ml={2} lh={2.3}>{t('pricing.common.net')}</Text>
                </>
              )}
            </div>
            <Text c="dimmed" size="sm" mb="lg">
              {plaTier === 0 ? t('pricing.pla.includedCaption') : t('pricing.pla.perCampaignCaption')}
            </Text>

            <div className="flex items-center justify-between mb-2">
              <Text size="xs" fw={600} c="gray.6" tt="uppercase" style={{ letterSpacing: '0.04em' }}>
                {t('pricing.pla.feedSizeLabel')}
              </Text>
              <Text size="sm" fw={700} c="dark">{plaTiers[plaTier].range}</Text>
            </div>
            <Slider
              value={plaTier}
              onChange={setPlaTier}
              min={0}
              max={PLA_PRICES.length - 1}
              step={1}
              color="brand"
              label={(v) => t('pricing.pla.sliderLabel', { range: plaTiers[v].range })}
              marks={plaTiers.map((tier, i) => ({ value: i, label: tier.mark }))}
              mb="lg"
              styles={{ markLabel: { fontSize: 10, marginTop: 4 }, thumb: { cursor: 'pointer' }, track: { cursor: 'pointer' } }}
            />
            <Text size="xs" c="dimmed" mt={8} mb="lg">
              {t('pricing.pla.sliderHint')}
            </Text>

            <div className="space-y-2.5 mb-6">
              <div className="flex items-center gap-2.5">
                <Check className="w-4 h-4 text-indigo-500 flex-shrink-0" weight="bold" />
                <Text size="sm" c="dark" fw={500}>
                  {plaTier === 0 ? t('pricing.pla.checkIncluded') : t('pricing.pla.checkFeed', { range: plaTiers[plaTier].range })}
                </Text>
              </div>
              <div className="flex items-center gap-2.5">
                <Check className="w-4 h-4 text-indigo-500 flex-shrink-0" weight="bold" />
                <Text size="sm" c="dimmed">{t('pricing.pla.checkAllFeatures')}</Text>
              </div>
            </div>

            <Button
              component={Link}
              to="/rejestracja"
              fullWidth
              size="md"
              radius="md"
              rightSection={<ArrowRight size={18} weight="bold" />}
            >
              {plaPrice === null ? t('pricing.pla.ctaContact') : t('pricing.pla.ctaStart')}
            </Button>
            <Text ta="center" c="dimmed" size="xs" mt="xs">{t('pricing.notes.trial')}</Text>
          </Paper>
        </Container>
      </section>
      )}

      {/* Pricing packages: Search / Meta */}
      {!plaMode && (
      <section className="bg-[#F6F5F4] py-10">
        <Container size="lg">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
            {PACKAGES.filter((pkg) => pkg.price !== null).map((pkg) => {
              const featured = pkg.popular;
              const isCustom = pkg.price === null;
              return (
                <Paper
                  key={pkg.key}
                  radius="lg"
                  p="lg"
                  withBorder
                  className={featured ? 'border-2 border-indigo-300 shadow-lg' : ''}
                  style={{ position: 'relative', display: 'flex', flexDirection: 'column' }}
                >
                  {featured && (
                    <Badge
                      variant="filled"
                      color="brand"
                      size="sm"
                      radius="sm"
                      style={{ position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)' }}
                    >
                      {t('pricing.packages.mostPopular')}
                    </Badge>
                  )}

                  <Text size="sm" c="indigo.6" fw={600} mb="xs">{t(`pricing.packages.${pkg.key}.name`)}</Text>

                  {/* Price */}
                  <div className="flex items-end gap-1" style={{ minHeight: 56 }}>
                    {isCustom ? (
                      <Text span fz={34} fw={900} c="dark" lh={1.3}>{t('pricing.common.custom')}</Text>
                    ) : (
                      <>
                        <Text span fz={44} fw={900} c="dark" lh={1.1}>{fmtNum(priceIn(pkg.price, currency))}</Text>
                        <Text span fz={18} c="dimmed" lh={1.7}>{currencySymbol(currency)}</Text>
                        <Text span size="sm" c="dimmed" ml={2} lh={2.3}>{t('pricing.common.net')}</Text>
                      </>
                    )}
                  </div>
                  <Text c="dimmed" size="sm" mb="md">
                    {isCustom ? t('pricing.packages.customScaleLower') : t('pricing.packages.perMonth')}
                  </Text>

                  {/* Koszt za kampanie, malejacy wraz z pakietem */}
                  <div className="mb-5" style={{ minHeight: 26 }}>
                    {!isCustom && (
                      <Badge variant="light" color="brand" radius="sm" size="md">
                        {t('pricing.packages.perCampaign', { price: formatMoney(priceIn(pkg.perCampaign, currency), currency) })}
                      </Badge>
                    )}
                  </div>

                  {/* Limity pakietu */}
                  <div className="space-y-2.5 mb-5">
                    <div className="flex items-center gap-2.5">
                      <Check className="w-4 h-4 text-indigo-500 flex-shrink-0" weight="bold" />
                      <Text size="sm" c="dark" fw={500}>
                        {pkg.campaigns ? t('pricing.packages.campaignsLimit', { count: pkg.campaigns }) : t('pricing.packages.campaignsUnlimited')}
                      </Text>
                    </div>
                    {pkg.budgetMaxDaily && (
                      <div className="flex items-center gap-2.5">
                        <Check className="w-4 h-4 text-indigo-500 flex-shrink-0" weight="bold" />
                        <Text size="sm" c="dark" fw={500}>
                          {t('pricing.packages.budgetLimit', { amount: pkg.budgetMaxDaily })}
                        </Text>
                      </div>
                    )}
                    <div className="flex items-center gap-2.5">
                      <Check className="w-4 h-4 text-indigo-500 flex-shrink-0" weight="bold" />
                      <Text size="sm" c="dark" fw={500}>
                        {pkg.accounts ? t('pricing.packages.accountsLimit', { count: pkg.accounts }) : t('pricing.packages.accountsUnlimited')}
                      </Text>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <Check className="w-4 h-4 text-indigo-500 flex-shrink-0" weight="bold" />
                      <Text size="sm" c="dimmed">{t('pricing.packages.allFeatures')}</Text>
                    </div>
                  </div>

                  <div className="flex-1" />

                  <Button
                    component={Link}
                    to={pkg.cta.to}
                    fullWidth
                    size="sm"
                    radius="md"
                    variant={pkg.cta.variant}
                    px="xs"
                    styles={{ label: { fontSize: 13, whiteSpace: 'nowrap' } }}
                  >
                    {t(`pricing.cta.${pkg.cta.ctaKey}`)}
                  </Button>
                  <Text ta="center" c="dimmed" size="xs" mt="xs">{t(`pricing.notes.${pkg.noteKey}`)}</Text>
                </Paper>
              );
            })}
          </div>

          {/* Agencja: szeroka karta pod pakietami */}
          <Paper
            radius="lg"
            p="xl"
            withBorder
            mt="md"
            style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 24 }}
          >
            <div style={{ minWidth: 200 }}>
              <Text size="sm" c="indigo.6" fw={600} mb={4}>{t('pricing.packages.agencja.name')}</Text>
              <Text fz={26} fw={900} c="dark" lh={1.2}>{t('pricing.agency.title')}</Text>
              <Text c="dimmed" size="sm" mt={4}>{t('pricing.agency.subtitle')}</Text>
            </div>

            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {agencyItems.map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-indigo-500 flex-shrink-0" weight="bold" />
                  <Text size="sm" c="dark" fw={500}>{item}</Text>
                </div>
              ))}
            </div>

            <Button
              component={Link}
              to="/kontakt"
              size="md"
              radius="md"
              variant="outline"
              rightSection={<ArrowRight size={18} weight="bold" />}
            >
              {t('pricing.cta.contact')}
            </Button>
          </Paper>

          <Text ta="center" c="dimmed" size="sm" mt="xl" maw={680} mx="auto">
            {t('pricing.packages.footnote')}
          </Text>
        </Container>
      </section>
      )}

      {/* Wszystko w kazdym pakiecie */}
      <section className="bg-white py-16 border-y border-gray-100">
        <Container size="md">
          <div className="text-center mb-10">
            <Badge variant="light" color="brand" size="lg" radius="xl" mb="md">
              {t('pricing.everything.badge')}
            </Badge>
            <Title order={2} mb="xs" fz={{ base: 24, md: 30 }}>
              {t('pricing.everything.title')}
            </Title>
            <Text c="dimmed" maw={540} mx="auto">
              {t('pricing.everything.subtitle')}
            </Text>
          </div>

          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 max-w-2xl mx-auto">
            {features.map((item, i) => (
              <div key={i} className="flex items-start gap-3 text-gray-700">
                <Check className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" weight="bold" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Pelne zarzadzanie wg typu kampanii */}
      <section className="bg-[#F6F5F4] py-16">
        <Container size="lg">
          <div className="text-center mb-10">
            <Badge variant="light" color="brand" size="lg" radius="xl" leftSection={<Robot size={16} weight="duotone" />} mb="md">
              {t('pricing.campaignTypes.badge')}
            </Badge>
            <Title order={2} mb="xs" fz={{ base: 24, md: 30 }}>
              {t('pricing.campaignTypes.title')}
            </Title>
            <Text c="dimmed" maw={620} mx="auto">
              {t('pricing.campaignTypes.subtitle')}
            </Text>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 items-stretch">
            {campaignTypes.map((ct, i) => {
              const Icon = CAMPAIGN_TYPE_ICONS[i];
              return (
                <Paper key={i} radius="lg" p="lg" withBorder className="flex flex-col h-full bg-white">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                      <Icon size={20} weight="duotone" className="text-indigo-600" />
                    </div>
                    <div>
                      <Text fw={700} c="dark" lh={1.2}>{ct.label}</Text>
                      <Text size="xs" c="indigo.6" fw={600}>{ct.tag}</Text>
                    </div>
                  </div>
                  <ul className="space-y-2 mt-1">
                    {ct.points.map((p, j) => (
                      <li key={j} className="flex items-start gap-2 text-gray-600 text-sm">
                        <Check size={14} weight="bold" className="text-indigo-500 flex-shrink-0 mt-0.5" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </Paper>
              );
            })}
          </div>

          <Text ta="center" c="dimmed" size="sm" mt="xl" maw={680} mx="auto">
            {t('pricing.campaignTypes.footnote')}
          </Text>
        </Container>
      </section>

      {/* Zastap agencje / dla agencji i freelancerow */}
      <section className="bg-white py-16 border-y border-gray-100">
        <Container size="lg">
          <div className="text-center mb-10">
            <Badge variant="light" color="brand" size="lg" radius="xl" leftSection={<Buildings size={16} weight="duotone" />} mb="md">
              {t('pricing.business.badge')}
            </Badge>
            <Title order={2} mb="xs" fz={{ base: 24, md: 30 }}>
              {t('pricing.business.title')}
            </Title>
            <Text c="dimmed" maw={620} mx="auto">
              {t('pricing.business.subtitle')}
            </Text>
          </div>

          <div className="grid md:grid-cols-2 gap-5 items-stretch">
            <Paper radius="lg" p="xl" withBorder className="flex flex-col h-full border-2 border-indigo-200">
              <Group gap="xs" mb="xs">
                <Lightning size={22} weight="duotone" className="text-indigo-600" />
                <Title order={4}>{t('pricing.business.forBusinessTitle')}</Title>
              </Group>
              <Text size="sm" c="dimmed" mb="md">{t('pricing.business.forBusinessDesc')}</Text>
              <ul className="space-y-2.5">
                {forBusiness.map((p, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-gray-700 text-sm">
                    <Check size={16} weight="bold" className="text-indigo-500 flex-shrink-0 mt-0.5" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </Paper>

            <Paper radius="lg" p="xl" withBorder className="flex flex-col h-full bg-[#F6F5F4]">
              <Group gap="xs" mb="xs">
                <Buildings size={22} weight="duotone" className="text-indigo-600" />
                <Title order={4}>{t('pricing.business.forAgencyTitle')}</Title>
              </Group>
              <Text size="sm" c="dimmed" mb="md">{t('pricing.business.forAgencyDesc')}</Text>
              <ul className="space-y-2.5">
                {forAgency.map((p, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-gray-700 text-sm">
                    <Check size={16} weight="bold" className="text-indigo-500 flex-shrink-0 mt-0.5" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </Paper>
          </div>

          <div className="text-center mt-8">
            <Button component={Link} to="/rejestracja" size="md" radius="md" color="brand" rightSection={<ArrowRight size={18} weight="bold" />}>
              {t('pricing.business.cta')}
            </Button>
          </div>
        </Container>
      </section>

      {/* Add-ons */}
      <section className="bg-[#F6F5F4] py-12">
        <Container size="md">
          <div className="text-center mb-8">
            <Title
              order={2}
              mb="xs"

              fz={{ base: 24, md: 30 }}
            >
              {t('pricing.addons.title')}
            </Title>
            <Text c="dimmed">
              {t('pricing.addons.subtitle')}
            </Text>
          </div>

          <Paper radius="lg" p="lg" withBorder style={{ borderColor: schedulerAddon ? 'var(--mantine-color-brand-4)' : undefined, transition: 'border-color 0.2s' }}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-4 flex-1">
                <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CalendarBlank size={20} weight="duotone" className="text-indigo-600" />
                </div>
                <div className="flex-1">
                  <Group gap="xs" mb={4}>
                    <Title order={4}>{t('pricing.addons.scheduler.title')}</Title>
                    <Badge variant="light" color="brand" size="xs">{t('pricing.addons.moduleBadge')}</Badge>
                  </Group>
                  <Text size="sm" c="dimmed" mb="sm">
                    {t('pricing.addons.scheduler.desc')}
                  </Text>
                  <ul className="space-y-1.5">
                    {schedulerPoints.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-600 text-sm">
                        <Check size={14} weight="bold" className="text-indigo-500 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="flex items-baseline gap-1 justify-end mb-1">
                  <Text span fz={28} fw={900} c="dark">+{fmtNum(priceIn(100, currency))}</Text>
                  <Text span fz={16} c="dimmed">{currencySymbol(currency)}</Text>
                </div>
                <Text size="xs" c="dimmed" mb="sm">{t('pricing.addons.scheduler.priceCaption')}</Text>
                <Switch
                  checked={schedulerAddon}
                  onChange={() => setSchedulerAddon(!schedulerAddon)}
                  color="brand"
                  size="md"
                  label={schedulerAddon ? t('pricing.addons.added') : t('pricing.addons.add')}
                  styles={{
                    track: { cursor: 'pointer' },
                    label: { fontSize: 13, fontWeight: 500, cursor: 'pointer' },
                  }}
                />
              </div>
            </div>
            {schedulerAddon && (
              <Paper bg="brand.0" p="sm" radius="md" mt="md">
                <Text size="xs" c="brand.7" fw={500}>
                  {t('pricing.addons.scheduler.activeNote')}
                </Text>
              </Paper>
            )}
          </Paper>
        </Container>
      </section>

      {/* Proactive agent 24/7 */}
      <section className="bg-[#F6F5F4] py-16">
        <Container size="sm">
          <div className="text-center mb-10">
            <Badge
              variant="light"
              color="brand"
              size="lg"
              radius="xl"
              leftSection={<ShieldCheck size={16} weight="duotone" />}
              mb="lg"
            >
              {t('pricing.monitoring.badge')}
            </Badge>
            <Title
              order={2}
              mb="xs"

              fz={{ base: 24, md: 30 }}
            >
              {t('pricing.monitoring.title')}
            </Title>
            <Text c="dimmed" maw={480} mx="auto">
              {t('pricing.monitoring.subtitle')}
            </Text>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-8">
            {monitoringChecks.map((check, i) => (
              <div key={i} className="flex items-center gap-3 py-2">
                <div className="w-6 h-6 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0">
                  <Check size={14} weight="bold" className="text-indigo-600" />
                </div>
                <Text size="md" c="dark">{check}</Text>
              </div>
            ))}
          </div>

          <Paper radius="lg" p="lg" className="bg-indigo-50 border border-indigo-200" ta="center">
            <Text size="sm" fw={600} c="indigo.6" mb="xs">
              {t('pricing.monitoring.cardTitle')}
            </Text>
            <Text size="sm" c="dimmed">
              {t('pricing.monitoring.cardDesc')}
            </Text>
          </Paper>
        </Container>
      </section>

      {/* Indywidualne wdrożenie - addon */}
      <section className="bg-[#F6F5F4] py-16">
        <Container size="xs">
          <div className="text-center mb-8">
            <Title
              order={2}
              mb="xs"

              fz={{ base: 24, md: 30 }}
            >
              {t('pricing.implementation.title')}
            </Title>
            <Text c="dimmed">
              {t('pricing.implementation.subtitle')}
            </Text>
          </div>

          <Paper radius="lg" p="lg" withBorder>
            <div className="flex items-start justify-between mb-4">
              <div>
                <Group gap="xs" mb={4}>
                  <Plus size={20} weight="bold" className="text-indigo-500" />
                  <Title order={4}>{t('pricing.implementation.name')}</Title>
                </Group>
                <div className="flex items-baseline gap-1">
                  <Text span fz={30} fw={900} c="dark">{fmtNum(priceIn(999, currency))}</Text>
                  <Text span fz={18} c="dimmed">{currencySymbol(currency)}</Text>
                  <Text span c="dimmed" ml={4}>{t('pricing.implementation.priceCaption')}</Text>
                </div>
              </div>
            </div>

            <ul className="space-y-2 mb-6">
              {implementationPoints.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-gray-600 text-[15px]">
                  <Check size={16} weight="bold" className="text-indigo-500 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <Button
              component={Link}
              to="/kontakt"
              fullWidth
              radius="md"
            >
              {t('pricing.implementation.cta')}
            </Button>
            <Text ta="center" c="dimmed" size="md" mt="xs">
              {t('pricing.implementation.contactNote')}
            </Text>
          </Paper>
        </Container>
      </section>

      {/* Trial info */}
      <section className="bg-[#F6F5F4] py-16">
        <Container size="sm" ta="center">
          <Badge
            variant="light"
            color="brand"
            size="lg"
            radius="xl"
            leftSection={<div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />}
            mb="lg"
          >
            {t('pricing.trial.badge')}
          </Badge>
          <Title
            order={2}
            mb="sm"

            fz={{ base: 24, md: 30 }}
          >
            {t('pricing.trial.title')}
          </Title>
          <Text c="dimmed" maw={480} mx="auto" mb="xl">
            {t('pricing.trial.desc')}
          </Text>
          <Button
            component={Link}
            to="/rejestracja"
            size="xl"
            radius="md"
            rightSection={<ArrowRight size={20} weight="bold" />}
            className="shadow-lg transition-shadow"
          >
            {t('pricing.trial.cta')}
          </Button>
        </Container>
      </section>

      {/* FAQ */}
      <section className="bg-[#F6F5F4] py-20">
        <Container size="sm">
          <Title
            order={2}
            ta="center"
            mb={48}

            fz={{ base: 24, md: 30 }}
          >
            {t('pricing.faq.title')}
          </Title>

          <div className="space-y-3">
            {faqItems.map((item, index) => (
              <Paper
                key={index}
                radius="md"
                withBorder
                className="overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="flex items-center justify-between w-full px-6 py-4 text-left"
                >
                  <Text fw={500}>{item.question}</Text>
                  {openFaq === index ? (
                    <CaretUp size={20} className="text-gray-400 flex-shrink-0" />
                  ) : (
                    <CaretDown size={20} className="text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <Text c="dimmed" size="md" lh={1.7}>{item.answer}</Text>
                  </div>
                )}
              </Paper>
            ))}
          </div>
        </Container>
      </section>

      <Footer />
    </div>
  );
}
